import { useDispatch, useSelector } from 'react-redux';
import { WorksheetEquipmentDropdown } from '../../Core';
import { Field, RadioGroup, Checkbox, NumericInput, TextOutput } from '../../../Core';
import { ListFieldSection, Grid, GridFieldSection } from '../../../Layout';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import React, { useEffect, useCallback, useState } from 'react';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import deepEquals from '../../../../Lib/Object/deepEquals';
import ids from '../../QestIds';
import { WorksheetBalanceFieldSet } from '../../Core/WorksheetEquipment';

const qestIds = [ids.oversizeDetermination.ASTM_C_136_D_6913];

let MeasuredOversizeDeterminationWorksheet = ({ prefixName, createWorksheetSelector = undefined }) => {
  const sieves = useSelector(createWorksheetSelector('Sieves'), deepEquals);
  const isSieveAvailable = useCallback(
    (sieveId) => sieves && sieves.some((s) => s.SieveId === sieveId && s.IsAvailable),
    [sieves]
  );

  const allSieves = useSelector(createWorksheetSelector('AllSieves'), deepEquals);
  const sieveMassType = useSelector(createWorksheetSelector('SieveMassType'));

  // Obtain data from the parent worksheet, assumed to be an MDD.
  // TODO: The Oversize Determination should have all of the information it needs to be performed
  // within its model
  const compactionMethodDeterminedBy = useSelector(
    createWorksheetSelector('data.Preparation.CompactionMethodDeterminedBy', false)
  );
  const compactionMethodId = useSelector(createWorksheetSelector('data.Preparation.CompactionMethod.Id', false));
  const oversizeSieveId = useSelector(
    createWorksheetSelector('data.Preparation.ScreeningASTM.OversizeSieve.Id', false)
  );

  const [prevCompactionMethodId, setPrevCompactionMethodId] = useState(compactionMethodId);
  const dispatch = useDispatch();

  const getLargestAvailableSieve = useCallback(() => {
    if (!sieves || !allSieves) return null;
    var largestSieveAndSize = sieves
      .filter((sieve) => sieve.IsAvailable && sieve.SieveId) // Include SieveId check the server has returned data as we set IsAvailable in this component.
      .map((s) => ({ sieve: s, size: parseFloat(allSieves.find((def) => def.Id === s.SieveId).Size.value) }))
      .reduce((prev, next) => (prev.size > next.size ? prev : next), { size: null });
    return largestSieveAndSize ? largestSieveAndSize.sieve : null;
  }, [sieves, allSieves]);

  const isSieveLargestAvailable = useCallback(
    (sieveId) => {
      const largestSieve = getLargestAvailableSieve();
      return largestSieve && largestSieve.SieveId === sieveId;
    },
    [getLargestAvailableSieve]
  );

  const sieveHasValue = useCallback(
    (sieveId) => {
      if (!sieves) return false;
      return sieves.some(
        (s) =>
          s.SieveId === sieveId &&
          (s.WetMassRetained || s.DryMassRetained || (isSieveLargestAvailable(s.SieveId) && s.WetMassPassing))
      );
    },
    [sieves, isSieveLargestAvailable]
  );

  const getRowHeaders = () => {
    if (!allSieves) return [];
    return allSieves.map((s) => s.Caption);
  };

  const getGridData = () => {
    if (!allSieves) return [];
    return allSieves.map((sieveDef, index) => [
      <Field
        key={`sieveCheckbox-${sieveDef.Id}`}
        input={<Checkbox />}
        name={`Sieves[${index}].IsAvailable`}
        confirm={({ value }) => value === false && sieveHasValue(sieveDef.Id, index)}
        confirmText="All values for the sieve will be removed."
        confirmButtonText="Remove Sieve"
        disabled={compactionMethodDeterminedBy === 'userdefined' && oversizeSieveId && oversizeSieveId === sieveDef.Id}
      />,
      <Field
        emph
        input={<WorksheetEquipmentDropdown />}
        label="Sieve"
        equipmentType="sieve"
        name={`Sieves[${index}].Sieve`}
        queryParams={{ SieveSize: `${sieveDef?.Size.value} ${sieveDef?.Size.uom}` }}
        disabled={!isSieveAvailable(sieveDef.Id)}
      />,
      <Field
        input={<NumericInput />}
        disabled={!isSieveAvailable(sieveDef.Id)}
        name={`Sieves[${index}].WetMassRetained`}
        label={`${sieveMassType === 'cumulative' ? 'Cumulative' : 'Individual'} Wet Mass Retained`}
      />,
      <Field
        input={<NumericInput />}
        disabled={!isSieveAvailable(sieveDef.Id)}
        name={`Sieves[${index}].DryMassRetained`}
        label={`${sieveMassType === 'cumulative' ? 'Cumulative' : 'Individual'} Dry Mass Retained`}
      />,
      <Field
        input={isSieveLargestAvailable(sieveDef.Id) ? <NumericInput /> : <TextOutput />} // top row always editable, others always not
        disabled={!isSieveAvailable(sieveDef.Id)}
        name={`Sieves[${index}].WetMassPassing`}
        label="Wet Mass Passing"
      />,
      <Field
        input={<TextOutput />}
        name={`Sieves[${index}].CumulativeWetMassRetainedPercent`}
        label="Cumulative Wet Mass Retained (%)"
      />,
      <Field
        input={<TextOutput />}
        name={`Sieves[${index}].CumulativeDryMassRetainedPercent`}
        label="Cumulative Dry Mass Retained (%)"
      />,
    ]);
  };

  useEffect(() => {
    const enableAndDisableSievesForUserDefinedCompaction = () => {
      // No effect unless user is setting the sieve themselves
      if (compactionMethodDeterminedBy === 'calculated' || !compactionMethodId) return;

      //TODO: somehow map this from available data / get this from the server
      // very ASTM D698 / 1557 specific as-is!
      const compactionMethodToSieveMap = {
        C: {
          sieveId: '19_0',
          sieveName: 'Preparation.AssociatedOversizeDetermination.MeasuredTest.Sieves[0].IsAvailable',
        },
        B: {
          sieveId: '9_5',
          sieveName: 'Preparation.AssociatedOversizeDetermination.MeasuredTest.Sieves[1].IsAvailable',
        },
        A: {
          sieveId: '4_75',
          sieveName: 'Preparation.AssociatedOversizeDetermination.MeasuredTest.Sieves[2].IsAvailable',
        },
      };

      const disableSieveIfNoData = (compactionMethodId) => {
        if (!sieveHasValue(compactionMethodToSieveMap[compactionMethodId].sieveId)) {
          dispatch(
            actions.setValue({
              name: prefixName(compactionMethodToSieveMap[compactionMethodId].sieveName),
              value: false,
              sync: true,
            })
          );
        }
      };

      const enableSieve = (compactionMethodId) => {
        dispatch(
          actions.setValue({
            name: prefixName(compactionMethodToSieveMap[compactionMethodId].sieveName),
            value: true,
            sync: true,
          })
        );
      };

      // Method has changed, so deselect any old ones (if they have no data) and select the new one
      if (!prevCompactionMethodId || prevCompactionMethodId !== compactionMethodId) {
        Object.keys(compactionMethodToSieveMap)
          .filter((m) => m !== compactionMethodId)
          .forEach((m) => disableSieveIfNoData(m));
        enableSieve(compactionMethodId);
        setPrevCompactionMethodId(compactionMethodId);
        return;
      }
    };
    enableAndDisableSievesForUserDefinedCompaction();
  }, [compactionMethodId, compactionMethodDeterminedBy, prevCompactionMethodId, dispatch, sieveHasValue, prefixName]);

  return (
    <>
      <ListFieldSection>
        <WorksheetBalanceFieldSet emph label="Balance" name="Equipment.Balance" />
      </ListFieldSection>
      <Field
        input={<RadioGroup />}
        label="Sieve masses are:"
        name="SieveMassType"
        options={['Cumulative', 'Individual'].map((e) => ({ label: e, value: e.toLowerCase() }))}
      />
      <GridFieldSection>
        <Grid
          cornerHeader="Sieve Size"
          colHeaders={[
            '',
            'Sieve',
            `${sieveMassType === 'cumulative' ? 'Cumulative' : 'Individual'} Wet Mass Retained (g)`,
            `${sieveMassType === 'cumulative' ? 'Cumulative' : 'Individual'} Dry Mass Retained (g)`,
            'Wet Mass Passing (g)',
            'Cumulative Wet Mass Retained (%)',
            'Cumulative Dry Mass Retained (%)',
          ]}
          colWidths={[30, 200, 130]}
          rowHeaders={getRowHeaders()}
          rowHeaderWidth={110}
          data={getGridData()}
        />
      </GridFieldSection>
      <ListFieldSection>
        <Field input={<TextOutput />} label="Dry Mass of Passing Material" name="FineDryMass" />
      </ListFieldSection>
    </>
  );
};

MeasuredOversizeDeterminationWorksheet = withInjectedProps(MeasuredOversizeDeterminationWorksheet);

MeasuredOversizeDeterminationWorksheet.qestIds = qestIds;

export default MeasuredOversizeDeterminationWorksheet;
export { MeasuredOversizeDeterminationWorksheet };
